import { ChangeEvent, KeyboardEvent, SetStateAction } from 'react';

import { formatTime, checkIfTimeIsInThePast } from '../date/date';

export const TimeErrorMessages = {
  InvalidTime: 'Invalid time',
  PastTime: 'Cannot use time in past',
  ResetError: '',
};

const increaseOrRoundUpMinutes = (minutes: number, increaseAmount: number) => {
  return minutes % 5 !== 0 ? (minutes = Math.ceil(minutes / 5) * 5) : (minutes += increaseAmount);
};

const decreaseOrRoundDownMinutes = (minutes: number, decreaseAmount: number) => {
  return minutes % 5 !== 0 ? (minutes = Math.floor(minutes / 5) * 5) : (minutes -= decreaseAmount);
};

export const handleChangingTimeValueByKeyPress = (
  keyCode: number,
  localTime: string,
  setTimeInput: (arg0: { date: Date }) => void,
  datePickerUpdate: unknown,
  bookingDate: string | number | Date,
  amountOfMinuteToChange = 5,
) => {
  let hour = parseInt(localTime?.slice(0, 2));
  let minutes = parseInt(localTime?.slice(3, 5));

  if (keyCode === 38) minutes = increaseOrRoundUpMinutes(minutes, amountOfMinuteToChange);
  if (keyCode === 40) minutes = decreaseOrRoundDownMinutes(minutes, amountOfMinuteToChange);

  // hourValueCompensate(minutes, hour);
  if (minutes < 0) {
    minutes = 55;
    hour -= 1;
  } else if (minutes > 59) {
    minutes = 0;
    hour += 1;
  }

  // if time is 00:00 and user presses arrow down, time will be 23:59 (previous day), booking date should subtract one day
  if (hour === -1) {
    hour = 23;

    if (datePickerUpdate) {
      const day = new Date(bookingDate);
      const minusOneDay = new Date(day.setDate(day.getDate() - 1));
      setTimeInput({ date: minusOneDay });
    }
  }

  // if time is 23:59 and user presses arrow up, time will be 00:00 (next day), booking date should add one day
  if (hour === 24) {
    hour = 0;

    if (datePickerUpdate) {
      const day = new Date(bookingDate);
      const nextDay = new Date(day.setDate(day.getDate() + 1));
      setTimeInput({ date: nextDay });
    }
  }

  const newTimeValue = `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

  return newTimeValue;
};

export const checkTimeWhenDateChanged = (
  timeValue: string,
  error: string | undefined,
  setError: { (value: SetStateAction<string>): void; (arg0: string): void },
  setFormError: ((v: unknown) => void) | undefined,
  bookingDate: Date | undefined,
  cannotUsePastTimeValueValidation: boolean,
  inactive: boolean,
) => {
  if (setFormError) setFormError(false);

  const today = new Date();
  today.setHours(parseInt(timeValue.slice(0, 2)));
  today.setMinutes(parseInt(timeValue.slice(3)));

  if (
    cannotUsePastTimeValueValidation &&
    checkIfTimeIsInThePast(today, new Date(), bookingDate) &&
    !inactive
  ) {
    if (setFormError) setFormError(true);
    setError(TimeErrorMessages.PastTime);
  } else {
    if (error === TimeErrorMessages.PastTime) setError('');
  }
};

const keyDownSetTimeInput = (
  inputId: string,
  hours: string,
  minutes: string,
  setTimeInput: (arg0: { [x: number]: Date }) => void,
) => {
  const date = new Date();
  date.setHours(parseInt(hours, 10));
  date.setMinutes(parseInt(minutes, 10));
  setTimeInput({ [inputId]: date });
};

export const timeInputFieldOnKeyDown = (
  e: KeyboardEvent<HTMLInputElement>,
  inputId: string,
  localTime: string | null | undefined,
  setLocalTime: { (value: SetStateAction<string | null | undefined>): void; (arg0: string): void },
  setTimeInput: () => void,
  datePickerUpdate: boolean | undefined,
  bookingDate: Date,
) => {
  if (
    localTime &&
    /^\d+$/.test(localTime.replace(':', '')) &&
    localTime.length > 3 &&
    (e.keyCode === 38 || e.keyCode === 40)
  ) {
    const newTimeValue = handleChangingTimeValueByKeyPress(
      e.keyCode,
      localTime,
      setTimeInput,
      datePickerUpdate,
      bookingDate,
    );
    setLocalTime(newTimeValue);

    if (setTimeInput) {
      const [hours, minutes] = newTimeValue.split(':');
      keyDownSetTimeInput(inputId, hours, minutes, setTimeInput);
    }
  }

  if (!localTime && (e.keyCode === 38 || e.keyCode === 40)) {
    const formattedCurrentTime = formatTime(new Date());
    const [hours, minutes] = formattedCurrentTime.split(':');
    keyDownSetTimeInput(inputId, hours, minutes, setTimeInput);
  }
};

export const handleTimeFieldInput = (
  e: ChangeEvent<HTMLInputElement>,
  _inputId: string,
  setLocalTime: { (value: SetStateAction<string | null | undefined>): void; (arg0: string): void },
  setError: { (value: SetStateAction<string>): void; (arg0: string): void },
  setFormError: ((v: unknown) => void) | undefined,
  setTimeInput: { (): void; (arg0: Date): void },
  cannotUsePastTimeValueValidation: boolean,
  bookingDate: Date | undefined,
  inactive: boolean,
) => {
  const input = e.target.value.replace(':', '');
  setLocalTime(input);

  if (input.length === 4 && parseFloat(input) < 2401) {
    if (setFormError) setFormError(false);
    const today = new Date();
    today.setHours(parseInt(input.slice(0, 2)));
    today.setMinutes(parseInt(input.slice(2)));

    setError(TimeErrorMessages.ResetError);

    setLocalTime(formatTime(today));
    setTimeInput(today);

    if (
      cannotUsePastTimeValueValidation &&
      checkIfTimeIsInThePast(today, new Date(), bookingDate) &&
      !inactive
    ) {
      setError(TimeErrorMessages.PastTime);
      if (setFormError) setFormError(true);
    }
  } else if (input.length === 4 && parseFloat(input) > 2400) {
    setError(TimeErrorMessages.InvalidTime);
    if (setFormError) setFormError(true);
  }
};
